import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { connect } from "react-redux";
import { formatDistanceToNow } from "date-fns";
import { parseDate } from "../utils/helpers/date";
import {
  Globe,
  MapPin,
  Calendar,
  User,
  Briefcase,
  Navigation,
  Star,
  ArrowUpDown,
  Link2,
  Info,
  Clock,
} from "lucide-react";
import InstagramLogo from "../assets/instagram.svg";
import FacebookLogo from "../assets/facebook.svg";
import LinkedinLogo from "../assets/linkedin.svg";
import YelpLogo from "../assets/yelp.svg";
import GoogleBusinessLogo from "../assets/google.svg";

import NavBar from "../components/layout/NavBar";
import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import AvatarComponent from "../components/Avatar";


/* ——— Theming ——— */
const theme = {
  colors: {
    primary: "hsl(217, 91%, 60%)",
    primaryLight: "hsl(217, 91%, 96%)",
    primaryText: "hsl(217, 91%, 50%)",
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc",
    surface: "#ffffff",
    border: "#e2e8f0",
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.04)",
    md: "0 4px 12px rgba(15, 23, 42, 0.06)",
  },
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  radius: "14px",
};

const GlobalStyle = createGlobalStyle`
  body { background: ${theme.colors.background}; font-family: ${theme.font}; }
`;

const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

/* ——— Layout ——— */
const Page = styled.div`
  min-height: 100vh;
  padding: 48px 0px;
  overflow-x: hidden;
`;
const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px;
  @media (max-width: 1024px) {
    padding: 0;
  }
`;

const HeaderWrap = styled.header`
  position: relative;
  margin: 0 0 24px;
  animation: ${fadeIn} 0.3s ease;
  background: ${theme.colors.surface};
  border-radius: ${theme.radius};
  box-shadow: none;
  overflow: hidden;
  border: 1px solid ${theme.colors.border};
  @media (max-width: 1024px) {
    margin: 0 0 16px;
    border-radius: 0;
    border-left: 0;
    border-right: 0;
  }
`;

const Banner = styled.div`
  height: 120px;
  background: ${theme.colors.background};
  position: relative;
`;

const HeaderInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  padding: 0 24px 20px;
  margin-top: -28px;
  @media (max-width: 768px) {
    gap: 16px;
    margin-top: -30px;
    padding: 0 24px 24px;
  }
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  flex: 1;
  h1 {
    margin: 0;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: ${theme.colors.textHeading};
    font-size: 1.5rem;
    line-height: 1.3;
    word-wrap: break-word;
    overflow-wrap: break-word;
    @media (max-width: 768px) {
      font-size: 1.3rem;
    }
  }
  .muted {
    color: ${theme.colors.textMuted};
    font-weight: 400;
    font-size: 0.95rem;
    line-height: 1.4;
  }
`;

const Ghost = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  color: ${theme.colors.textBody};
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  padding: 7px 10px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: ${theme.colors.primaryLight};
    color: ${theme.colors.primaryText};
    border-color: ${theme.colors.primary};
  }
`;

const MetaRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  padding: 0 24px 20px;
  justify-content: center;
  @media (max-width: 768px) {
    padding: 0 24px 24px;
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => (p.$size === "lg" ? "10px" : p.$size === "sm" ? "6px" : "8px")};
  padding: ${(p) =>
    p.$size === "lg" ? "8px 14px" : p.$size === "sm" ? "4px 8px" : "5px 10px"};
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  font-weight: ${(p) => (p.$size === "lg" ? 600 : 500)};
  font-size: ${(p) =>
    p.$size === "lg" ? "0.86rem" : p.$size === "sm" ? "0.74rem" : "0.78rem"};
  color: ${theme.colors.textBody};
  line-height: 1.1;
  max-width: 100%;
  box-shadow: none;
  transition: border-color 0.2s ease, background 0.2s ease;
  align-self: flex-start;
  &:hover {
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primaryLight};
  }
  @media (max-width: 768px) {
    padding: ${(p) =>
      p.$size === "lg" ? "7px 12px" : p.$size === "sm" ? "4px 8px" : "5px 9px"};
    font-size: ${(p) =>
      p.$size === "lg" ? "0.82rem" : p.$size === "sm" ? "0.72rem" : "0.76rem"};
  }
`;

const BrandIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const PortfolioCallout = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${theme.colors.primaryLight};
  border: 1px dashed ${theme.colors.border};
  color: ${theme.colors.textBody};
  font-size: 0.9rem;
`;

const PortfolioCalloutBtn = styled.button`
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid ${theme.colors.primary};
  background: ${theme.colors.surface};
  color: ${theme.colors.primaryText};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${theme.colors.primaryLight};
  }
`;

const PortfolioText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const PortfolioTitle = styled.span`
  font-weight: 600;
  font-size: 0.85rem;
  color: ${theme.colors.textHeading};
  line-height: 1.1;
`;

const PortfolioSubtitle = styled.span`
  font-size: 0.8rem;
  color: ${theme.colors.textMuted};
  line-height: 1.1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 280px;
`;

/* ——— Tabs ——— */
const TabsBar = styled.div`
  display: flex;
  gap: 20px;
  padding: 0 32px 0;
  border-top: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  width: 100%;
  box-sizing: border-box;
  @media (max-width: 768px) {
    padding: 8px 16px 12px;
    gap: 8px;
    border-top: 1px solid ${theme.colors.border};
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;
const TabBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: 0;
  cursor: pointer;
  font-weight: 600;
  padding: 12px 0;
  position: relative;
  font-size: 1rem;
  transition: color 0.2s ease;
  color: ${(p) =>
    p.active ? theme.colors.textHeading : theme.colors.textMuted};
  &:hover {
    color: ${theme.colors.textHeading};
  }
  &:after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: ${(p) => (p.active ? theme.colors.primary : "transparent")};
    transition: background 0.2s ease;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
    padding: 8px 6px;
    border-radius: 10px;
    border: 1px solid
      ${(p) => (p.active ? theme.colors.primaryLight : theme.colors.border)};
    background: ${(p) => (p.active ? theme.colors.primaryLight : "#ffffff")};
    color: ${(p) => (p.active ? theme.colors.primary : theme.colors.textBody)};
    font-size: 0.8rem;
    line-height: 1.2;
    flex-direction: column;
    gap: 2px;
    line-height: 1.2;

    &:after {
      display: none;
    }
  }
`;

const TabMain = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 0.78rem;
  }
`;

const TabSub = styled.span`
  font-size: 0.74rem;
  font-weight: 500;
  color: ${theme.colors.textMuted};
  line-height: 1.1;

  @media (max-width: 768px) {
    color: inherit;
    opacity: 0.8;
  }
`;

/* ——— Content ——— */
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 0;
`;

const AboutMain = styled.div`
  display: grid;
  gap: 26px;
`;

const ServicesTagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  overflow: hidden;
  max-height: ${(p) => (p.$expanded ? "none" : "132px")};
`;

const ViewMoreButton = styled.button`
  margin-top: 10px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${theme.colors.border};
  background: #ffffff;
  color: ${theme.colors.textBody};
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primaryLight};
    color: ${theme.colors.primary};
  }
`;

const Card = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  box-shadow: none;
  padding: 24px;
  animation: ${fadeIn} 0.3s ease;
  transition: border-color 0.2s ease;
  @media (max-width: 768px) {
    padding: 24px;
    border-radius: 0;
    border-left: 0;
    border-right: 0;
  }
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 16px;
  color: ${theme.colors.textHeading};
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.3;
`;


/* ——— Tasks list ——— */
const List = styled.div`
  display: grid;
  gap: 12px;
`;
const Item = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  background: #fff;
  padding: 14px;
  transition: border-color 0.12s ease, background 0.12s ease;
  box-shadow: none;
  &:hover {
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primaryLight};
  }
`;
const ItemTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const ItemTitle = styled.div`
  font-weight: 600;
  color: ${theme.colors.textHeading};
`;
const Price = styled.div`
  margin-left: auto;
  font-weight: 700;
  color: ${theme.colors.textBody};
`;
const ItemMeta = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 0.82rem;
  color: ${theme.colors.textMuted};
`;
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => (p.$size === "lg" ? "7px" : "6px")};
  padding: ${(p) => (p.$size === "lg" ? "6px 10px" : "5px 9px")};
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  font-weight: 600;
  font-size: ${(p) => (p.$size === "lg" ? "0.8rem" : "0.76rem")};
  line-height: 1.1;
  @media (max-width: 768px) {
    padding: ${(p) => (p.$size === "lg" ? "5px 9px" : "4px 8px")};
    font-size: ${(p) => (p.$size === "lg" ? "0.78rem" : "0.74rem")};
  }
`;

/* ——— Small helpers ——— */
const Empty = styled.div`
  padding: 16px;
  text-align: center;
  color: ${theme.colors.textMuted};
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  background: #ffffff;
`;

const SortRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  select {
    height: 34px;
    border: 1px solid ${theme.colors.border};
    border-radius: 8px;
    padding: 0 10px;
    font-weight: 600;
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 2px ${theme.colors.primaryLight};
    }
  }
`;

const Skeleton = styled.div`
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9 0%, #f8fafc 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  height: ${(p) => p.h || "14px"};
  width: ${(p) => p.w || "100%"};
  max-width: 100%;
  box-sizing: border-box;
`;

/* ——— Reviews Section ——— */
const ReviewsSection = styled.div`
  margin-top: 0;
`;

const ReviewsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const ReviewsTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ReviewCount = styled.span`
  font-size: 0.875rem;
  color: ${theme.colors.textMuted};
  font-weight: 400;
`;

const ReviewCard = styled.div`
  padding: 16px;
  background: ${theme.colors.background};
  border-radius: 12px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const ReviewerAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
`;

const ReviewerInfo = styled.div`
  flex: 1;
`;

const ReviewerName = styled.div`
  font-weight: 500;
  color: ${theme.colors.textHeading};
  font-size: 0.9rem;
`;

const ReviewMeta = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StarRating = styled.div`
  display: flex;
  gap: 2px;
  svg {
    width: 16px;
    height: 16px;
    color: #f59e0b;
    fill: #f59e0b;
  }
  svg.empty {
    color: #d1d5db;
    fill: none;
  }
`;

const ReviewFeedback = styled.p`
  margin: 8px 0 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${theme.colors.textBody};
`;

const ReviewTaskLink = styled(Link)`
  font-size: 0.8rem;
  color: ${theme.colors.primaryText};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

/* ——— Page ——— */
const UserProfilePage = ({ userState }) => {
  const { id } = useParams(); // poster user id
  const nav = useNavigate();

  const getProfileIcon = (type) => {
    switch (type) {
      case "instagram":
        return { src: InstagramLogo, alt: "Instagram" };
      case "facebook":
        return { src: FacebookLogo, alt: "Facebook" };
      case "linkedin":
        return { src: LinkedinLogo, alt: "LinkedIn" };
      case "google_business":
        return { src: GoogleBusinessLogo, alt: "Google Business Profile" };
      case "yelp":
        return { src: YelpLogo, alt: "Yelp" };
      default:
        return null;
    }
  };

  const getProfileLabel = (type) => {
    switch (type) {
      case "instagram":
        return "Instagram";
      case "facebook":
        return "Facebook";
      case "linkedin":
        return "LinkedIn";
      case "google_business":
        return "Google Business";
      case "yelp":
        return "Yelp";
      default:
        return "Website";
    }
  };

  const getDisplayText = (type, url) => {
    if (!url) return "";
    try {
      const parsed = new URL(url);
      const host = parsed.hostname.replace(/^www\./, "");
      const path = parsed.pathname.replace(/\/+$/, "");
      const isSocial =
        type === "instagram" ||
        type === "facebook" ||
        type === "linkedin";
      if (isSocial && path && path !== "/") {
        const handle = path.split("/").filter(Boolean)[0] || "";
        return handle ? `@${handle}` : host;
      }
      if (path && path !== "/") {
        return `${host}${path}`;
      }
      return host;
    } catch {
      return url.replace(/^https?:\/\//, "");
    }
  };

  const getCategoryName = (categoryId) => {
    if (!categoryId) return null;
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : null;
  };

  const [profile, setProfile] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);
  const [isLoadingCompletedTasks, setIsLoadingCompletedTasks] = useState(true);

  const [tab, setTab] = useState("about"); // about | tasks | completed
  const [sortBy, setSortBy] = useState("date_desc"); // date_desc|date_asc|budget_desc|budget_asc
  const [completedSortBy, setCompletedSortBy] = useState("date_desc");
  const [reviews, setReviews] = useState([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);
  const [showAllServiceTags, setShowAllServiceTags] = useState(false);
  const [hasServiceTagsOverflow, setHasServiceTagsOverflow] = useState(false);
  const serviceTagsRef = useRef(null);
  const isOwnProfile =
    Number(id) ===
    Number(userState?.user?.user_id || userState?.user?.id || userState?.user_id);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setIsLoadingProfile(true);
      try {
        const { data } = await request(
          "POST",
          API_CONSTANT_MAP.fetch_user_profile,
          { user_id: Number(id) }
        );
        if (mounted && data?.success) setProfile(data.user);
      } catch (e) {
        console.error("Failed to load profile", e);
      } finally {
        if (mounted) setIsLoadingProfile(false);
      }
    };

    load();
    return () => (mounted = false);
  }, [id]);

  useEffect(() => {
    let mounted = true;
    const loadTasks = async () => {
      setIsLoadingTasks(true);
      try {
        const { data } = await request(
          "POST",
          API_CONSTANT_MAP.fetch_user_profile_tasks,
          {
            user_id: Number(id),
            limit: 50,
          }
        );
        if (mounted && data?.success) {
          setTasks(data.tasks || []);
          setCompletedTasks(data.completed_tasks || []);
        }
      } catch (e) {
        console.error("Failed to load tasks", e);
      } finally {
        if (mounted) {
          setIsLoadingTasks(false);
          setIsLoadingCompletedTasks(false);
        }
      }
    };
    loadTasks();
    return () => (mounted = false);
  }, [id]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await request(
          API_CONSTANT_MAP.fetch_categories,
          "GET"
        );
        if (response?.success) {
          setCategories(response.categories || []);
        }
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch reviews
  useEffect(() => {
    let mounted = true;
    const loadReviews = async () => {
      setIsLoadingReviews(true);
      try {
        const { data } = await request(
          "POST",
          API_CONSTANT_MAP.fetch_user_reviews,
          { user_id: id }
        );
        if (mounted && data?.success) {
          setReviews(data.reviews || []);
        }
      } catch (e) {
        console.error("Failed to load reviews", e);
      } finally {
        if (mounted) setIsLoadingReviews(false);
      }
    };
    loadReviews();
    return () => (mounted = false);
  }, [id]);

  const fullName = useMemo(() => {
    if (!profile) return "";
    const first = profile.first_name || "";
    const last = profile.last_name || "";
    return [first, last].filter(Boolean).join(" ");
  }, [profile]);

  // Sorting functions
  const sortTasks = (tasks, sortType) => {
    const sorted = [...tasks];
    switch (sortType) {
      case "date_desc":
        return sorted.sort(
          (a, b) => parseDate(b.created_at) - parseDate(a.created_at)
        );
      case "date_asc":
        return sorted.sort(
          (a, b) => parseDate(a.created_at) - parseDate(b.created_at)
        );
      case "budget_desc":
        return sorted.sort((a, b) => (b.budget || 0) - (a.budget || 0));
      case "budget_asc":
        return sorted.sort((a, b) => (a.budget || 0) - (b.budget || 0));
      default:
        return sorted;
    }
  };

  const sortCompletedTasks = (tasks, sortType) => {
    const sorted = [...tasks];
    switch (sortType) {
      case "date_desc":
        return sorted.sort(
          (a, b) =>
            parseDate(b.completed_at || b.updated_at) -
            parseDate(a.completed_at || a.updated_at)
        );
      case "date_asc":
        return sorted.sort(
          (a, b) =>
            parseDate(a.completed_at || a.updated_at) -
            parseDate(b.completed_at || b.updated_at)
        );
      default:
        return sorted;
    }
  };

  // Memoized sorted arrays
  const sortedTasks = useMemo(() => sortTasks(tasks, sortBy), [tasks, sortBy]);
  const sortedCompletedTasks = useMemo(
    () => sortCompletedTasks(completedTasks, completedSortBy),
    [completedTasks, completedSortBy]
  );
  const parsedAverageRating = Number(profile?.average_rating);
  const averageRatingValue = Number.isFinite(parsedAverageRating)
    ? parsedAverageRating.toFixed(1)
    : null;
  const totalReviewCount = profile?.total_reviews || reviews.length;

  useEffect(() => {
    setShowAllServiceTags(false);
  }, [id]);

  useEffect(() => {
    const checkOverflow = () => {
      const el = serviceTagsRef.current;
      if (!el) {
        setHasServiceTagsOverflow(false);
        return;
      }
      setHasServiceTagsOverflow(el.scrollHeight > 132);
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [profile?.work_categories]);

  return (
    <>
      <GlobalStyle />
      <Page>
        <NavBar user={userState.user} />

        <Container>
          {/* Header */}
          <HeaderWrap>
            <Banner />
            {isLoadingProfile ? (
              <>
                {/* Header Skeleton */}
                <HeaderInner>
                  <div>
                    <Skeleton
                      h="80px"
                      w="80px"
                      style={{ borderRadius: "50%" }}
                    />
                  </div>
                  <Title>
                    <Skeleton
                      h="28px"
                      w="200px"
                      style={{ marginBottom: "8px" }}
                    />
                    <Skeleton h="16px" w="150px" />
                  </Title>
                </HeaderInner>

                <MetaRow>
                  <Skeleton
                    h="28px"
                    w="80px"
                    style={{ borderRadius: "999px" }}
                  />
                  <Skeleton
                    h="28px"
                    w="90px"
                    style={{ borderRadius: "999px" }}
                  />
                  <Skeleton
                    h="28px"
                    w="120px"
                    style={{ borderRadius: "999px" }}
                  />
                </MetaRow>

                <TabsBar>
                  <Skeleton h="20px" w="50px" />
                  <Skeleton h="20px" w="100px" />
                </TabsBar>
              </>
            ) : (
              <>
                <HeaderInner>
                  <AvatarComponent
                    src={profile?.avatar}
                    firstName={profile?.first_name}
                    lastName={profile?.last_name}
                    name={profile?.username}
                    userId={Number(id)}
                    size="100px"
                    fontSize="1.8rem"
                    style={{
                      border: '3px solid #ffffff',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(255, 255, 255, 0.8)',
                      zIndex: 10,
                      position: 'relative',
                    }}
                  />
                  <Title style={{ textAlign: 'inherit' }}>
                    <div style={{ 
                      fontSize: '1.5rem', 
                      fontWeight: '700', 
                      color: '#0f172a',
                      margin: '0 0 4px 0',
                      lineHeight: '1.2',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      maxWidth: '100%'
                    }}>
                      {(fullName || "User").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                    </div>
                    <div className="muted" style={{ 
                      fontSize: '1rem',
                      fontWeight: '400',
                      color: '#6b7280',
                      margin: '0'
                    }}>
                      Member since{" "}
                      {profile?.created_at
                        ? formatDistanceToNow(parseDate(profile.created_at), {
                            addSuffix: true,
                          })
                        : "—"}
                    </div>
                  </Title>
                </HeaderInner>

                <MetaRow>
                  {averageRatingValue && (
                    <Chip
                      $size="sm"
                      style={{
                        background: "#fffbeb",
                        color: "#92400e",
                        borderColor: "#fef3c7",
                        fontWeight: "600",
                      }}
                    >
                      <Star
                        size={14}
                        style={{ color: "#f59e0b", fill: "#f59e0b" }}
                      />
                      {averageRatingValue} ({totalReviewCount})
                    </Chip>
                  )}
                  {profile?.location?.city && profile?.location?.state_id && (
                    <Chip $size="sm">
                      <MapPin size={14} /> 
                      {profile.location.city}, {profile.location.state_id}
                    </Chip>
                  )}
                  {profile?.website && (
                    <Chip
                      $size="sm"
                      as="a"
                      href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ cursor: 'pointer' }}
                    >
                      <Globe size={14} /> {(() => {
                        try {
                          const url = profile.website.startsWith('http') ? profile.website : `https://${profile.website}`;
                          return new URL(url).hostname.replace('www.', '');
                        } catch {
                          return profile.website;
                        }
                      })()}
                    </Chip>
                  )}
                  {profile?.last_activity_at && (
                    <Chip $size="sm" style={{
                      background: '#f0fdf4',
                      color: '#10b981',
                      borderColor: '#bbf7d0',
                      fontWeight: '600'
                    }}>
                      <Clock size={14} />
                      Active {formatDistanceToNow(parseDate(profile.last_activity_at), {
                        addSuffix: true,
                      })}
                    </Chip>
                  )}
                </MetaRow>

                <TabsBar>
                  <TabBtn
                    active={tab === "about"}
                    onClick={() => setTab("about")}
                  >
                    <TabMain>About</TabMain>
                  </TabBtn>
                  <TabBtn
                    active={tab === "tasks"}
                    onClick={() => setTab("tasks")}
                  >
                    <TabMain>Posted</TabMain>
                    <TabSub>{tasks.length} tasks</TabSub>
                  </TabBtn>
                  <TabBtn
                    active={tab === "completed"}
                    onClick={() => setTab("completed")}
                  >
                    <TabMain>Completed</TabMain>
                    <TabSub>{completedTasks.length} jobs</TabSub>
                  </TabBtn>
                </TabsBar>
              </>
            )}
          </HeaderWrap>

          <ContentGrid>
            {/* About */}
            {tab === "about" && (
              <Card>
                <SectionTitle>
                  <User size={18} /> About {(fullName || "this user").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                </SectionTitle>
                {isLoadingProfile ? (
                  <div style={{ display: "grid", gap: 16 }}>
                    <div style={{ display: "grid", gap: 8 }}>
                      <Skeleton h="16px" w="100%" />
                      <Skeleton h="16px" w="95%" />
                      <Skeleton h="16px" w="88%" />
                      <Skeleton h="16px" w="75%" />
                    </div>

                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 12,
                        }}
                      >
                        <Skeleton h="18px" w="18px" />
                        <Skeleton h="18px" w="80px" />
                      </div>
                      <div
                        style={{ display: "flex", gap: 10, flexWrap: "wrap" }}
                      >
                        <Skeleton
                          h="28px"
                          w="120px"
                          style={{ borderRadius: "999px" }}
                        />
                        <Skeleton
                          h="28px"
                          w="140px"
                          style={{ borderRadius: "999px" }}
                        />
                        <Skeleton
                          h="28px"
                          w="100px"
                          style={{ borderRadius: "999px" }}
                        />
                      </div>
                    </div>
                  </div>
                ) : profile ? (
                  <AboutMain>
                      {/* Bio Section */}
                      <div>
                        <p style={{
                          margin: 0,
                          lineHeight: 1.7,
                          color: profile.bio ? theme.colors.textBody : theme.colors.textMuted,
                          fontSize: "1rem",
                          maxWidth: "800px",
                          fontStyle: profile.bio ? "normal" : "italic"
                        }}>
                          {profile.bio || "No bio available yet."}
                        </p>
                      </div>

                      {/* Reviews Section */}
                      <ReviewsSection>
                        <ReviewsHeader>
                          <ReviewsTitle>
                            <Star size={18} />
                            Reviews
                            {!isLoadingReviews && (
                              <ReviewCount>({totalReviewCount})</ReviewCount>
                            )}
                          </ReviewsTitle>
                        </ReviewsHeader>

                        {isLoadingReviews ? (
                          <div style={{ display: "grid", gap: 12 }}>
                            {[1, 2].map((i) => (
                              <ReviewCard key={i}>
                                <div style={{ display: "flex", gap: 12, marginBottom: 8 }}>
                                  <Skeleton h="36px" w="36px" style={{ borderRadius: "50%" }} />
                                  <div style={{ flex: 1 }}>
                                    <Skeleton h="14px" w="120px" style={{ marginBottom: 4 }} />
                                    <Skeleton h="12px" w="80px" />
                                  </div>
                                </div>
                                <Skeleton h="14px" w="100%" />
                              </ReviewCard>
                            ))}
                          </div>
                        ) : reviews.length > 0 ? (
                          <>
                            {reviews.map((review) => (
                              <ReviewCard key={review.id}>
                                <ReviewHeader>
                                  <ReviewerAvatar
                                    src={review.reviewer?.avatar || "/default-avatar.png"}
                                    alt=""
                                  />
                                  <ReviewerInfo>
                                    <ReviewerName>
                                      <Link to={`/users/${review.reviewer?.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                                        {review.reviewer?.first_name} {review.reviewer?.last_name}
                                      </Link>
                                    </ReviewerName>
                                    <ReviewMeta>
                                      <StarRating>
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <Star
                                            key={star}
                                            size={14}
                                            className={star <= review.rating ? "" : "empty"}
                                          />
                                        ))}
                                      </StarRating>
                                      <span>
                                        {formatDistanceToNow(parseDate(review.created_at), { addSuffix: true })}
                                      </span>
                                    </ReviewMeta>
                                  </ReviewerInfo>
                                </ReviewHeader>
                                {review.feedback && (
                                  <ReviewFeedback>{review.feedback}</ReviewFeedback>
                                )}
                                {review.task && (
                                  <div style={{ marginTop: 8 }}>
                                    <ReviewTaskLink to={`/tasks/${review.task.id}`}>
                                      For: {review.task.title}
                                    </ReviewTaskLink>
                                  </div>
                                )}
                              </ReviewCard>
                            ))}
                          </>
                        ) : (
                          <Empty>No reviews yet</Empty>
                        )}
                      </ReviewsSection>

                      {/* Services Offered */}
                      {Array.isArray(profile.work_categories) && profile.work_categories.length > 0 && (
                        <div>
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "8px"
                          }}>
                            <Briefcase size={18} />
                            <span style={{
                              fontWeight: "600",
                              color: theme.colors.textHeading,
                              fontSize: "1.1rem"
                            }}>
                              Services Offered
                            </span>
                          </div>
                          <div style={{
                            fontSize: "0.85rem",
                            color: theme.colors.textMuted,
                            marginBottom: "16px",
                            fontStyle: "italic"
                          }}>
                            Based on notification preferences
                          </div>
                          <ServicesTagsList
                            ref={serviceTagsRef}
                            $expanded={showAllServiceTags}
                          >
                            {profile.work_categories.map((category) => (
                              <Chip
                                $size="lg"
                                key={category.id}
                                style={{
                                  background: theme.colors.primaryLight,
                                  color: theme.colors.primaryText,
                                  border: `1px solid ${theme.colors.primary}30`,
                                  fontWeight: "600"
                                }}
                              >
                                <Briefcase size={16} />
                                {category.name}
                              </Chip>
                            ))}
                          </ServicesTagsList>
                          {hasServiceTagsOverflow && (
                            <ViewMoreButton
                              type="button"
                              onClick={() => setShowAllServiceTags((prev) => !prev)}
                            >
                              {showAllServiceTags ? "View less" : "View more"}
                            </ViewMoreButton>
                          )}
                        </div>
                      )}

                      {/* Portfolio Links */}
                      <div>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "16px"
                        }}>
                          <Link2 size={18} />
                          <span style={{
                            fontWeight: "600",
                            color: theme.colors.textHeading,
                            fontSize: "1rem"
                          }}>
                            Portfolio
                          </span>
                        </div>
                        {Array.isArray(profile.portfolio_links) && profile.portfolio_links.length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            {profile.portfolio_links.map((item, idx) => {
                              const url =
                                typeof item === "string"
                                  ? item
                                  : (item && item.url) || "";
                              if (!url) return null;
                              const type =
                                typeof item === "string" ? "other" : item?.type;
                              const icon = getProfileIcon(type);
                              const label = getProfileLabel(type);
                              const displayText = getDisplayText(type, url);
                              return (
                              <Chip
                                key={idx}
                                as="a"
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  justifyContent: "flex-start",
                                  textDecoration: "none"
                                }}
                                title={url}
                              >
                                {icon ? (
                                  <BrandIcon src={icon.src} alt={icon.alt} />
                                ) : (
                                  <Globe size={16} />
                                )}
                                <PortfolioText>
                                  <PortfolioTitle>{label}</PortfolioTitle>
                                  <PortfolioSubtitle>{displayText}</PortfolioSubtitle>
                                </PortfolioText>
                              </Chip>
                            );
                            })}
                          </div>
                        ) : (
                          isOwnProfile && (
                            <PortfolioCallout>
                              <span>Add your business profiles to help clients trust you.</span>
                              <PortfolioCalloutBtn onClick={() => nav("/account")}>
                                Add Links
                              </PortfolioCalloutBtn>
                            </PortfolioCallout>
                          )
                        )}
                      </div>
                  </AboutMain>
                ) : (
                  <Empty>Couldn't load this profile.</Empty>
                )}
              </Card>
            )}

            {/* Tasks */}
            {tab === "tasks" && (
              <Card>
                <SectionTitle>
                  <Briefcase size={18} /> Posted Tasks
                </SectionTitle>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    color: theme.colors.primaryText,
                    fontSize: "0.9rem",
                    marginBottom: "16px",
                    padding: "12px 16px",
                    background: theme.colors.primaryLight,
                    borderRadius: "10px",
                    border: `1px solid ${theme.colors.primary}20`,
                  }}
                >
                  <Info size={16} style={{ marginTop: "1px", flexShrink: 0 }} />
                  <span>
                    Tasks that {fullName || "this user"} has posted on the
                    platform looking for service providers.
                  </span>
                </div>

                <SortRow>
                  <div
                    style={{ color: theme.colors.textMuted, fontWeight: 700 }}
                  >
                    {isLoadingTasks ? (
                      <Skeleton h="16px" w="80px" />
                    ) : (
                      `${sortedTasks.length} tasks`
                    )}
                  </div>
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <ArrowUpDown size={16} />
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="date_desc">Newest</option>
                      <option value="date_asc">Oldest</option>
                      <option value="budget_desc">Budget: High to Low</option>
                      <option value="budget_asc">Budget: Low to High</option>
                    </select>
                  </div>
                </SortRow>

                {isLoadingTasks ? (
                  <List>
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: "14px",
                          padding: "14px",
                          background: "#fff",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            marginBottom: "8px",
                          }}
                        >
                          <Skeleton h="18px" w="200px" />
                          <div style={{ marginLeft: "auto" }}>
                            <Skeleton h="18px" w="60px" />
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <Skeleton
                            h="24px"
                            w="80px"
                            style={{ borderRadius: "999px" }}
                          />
                          <Skeleton
                            h="24px"
                            w="100px"
                            style={{ borderRadius: "999px" }}
                          />
                          <Skeleton
                            h="24px"
                            w="70px"
                            style={{ borderRadius: "999px" }}
                          />
                        </div>
                      </div>
                    ))}
                  </List>
                ) : sortedTasks.length ? (
                  <List>
                    {sortedTasks.map((t) => (
                      <Item key={t.id} to={`/tasks/${t.id}`}>
                        <ItemTop>
                          <ItemTitle style={{ textTransform: "capitalize" }}>
                            {t.title}
                          </ItemTitle>
                          <Price>${t.budget}</Price>
                        </ItemTop>
                        <ItemMeta>
                          {getCategoryName(t.category_id) && (
                            <Badge style={{ 
                              background: theme.colors.primaryLight, 
                              color: theme.colors.primary,
                              fontWeight: '600'
                            }}>
                              <Briefcase size={14} />
                              {getCategoryName(t.category_id)}
                            </Badge>
                          )}
                          <Badge>
                            <MapPin size={14} />
                            {t.location ||
                              (t.task_type === "online"
                                ? "Remote"
                                : "Location not available")}
                          </Badge>
                          <Badge>
                            <Calendar size={14} />
                            {formatDistanceToNow(parseDate(t.created_at), {
                              addSuffix: true,
                            })}
                          </Badge>
                          {typeof t.distance_miles === "number" && (
                            <Badge>
                              <Navigation size={14} />
                              {t.distance_miles.toFixed(1)} mi
                            </Badge>
                          )}
                        </ItemMeta>
                      </Item>
                    ))}
                  </List>
                ) : (
                  <Empty>No tasks yet.</Empty>
                )}
              </Card>
            )}

            {/* Completed Work */}
            {tab === "completed" && (
              <Card>
                <SectionTitle>
                  <Star size={18} /> Completed Work
                </SectionTitle>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    color: theme.colors.primaryText,
                    fontSize: "0.9rem",
                    marginBottom: "16px",
                    padding: "12px 16px",
                    background: theme.colors.primaryLight,
                    borderRadius: "10px",
                    border: `1px solid ${theme.colors.primary}20`,
                  }}
                >
                  <Info size={16} style={{ marginTop: "1px", flexShrink: 0 }} />
                  <span>
                    Tasks that {fullName || "this user"} has successfully
                    completed for other users.
                  </span>
                </div>

                <SortRow>
                  <div
                    style={{ color: theme.colors.textMuted, fontWeight: 700 }}
                  >
                    {isLoadingCompletedTasks ? (
                      <Skeleton h="16px" w="100px" />
                    ) : (
                      `${sortedCompletedTasks.length} completed tasks`
                    )}
                  </div>
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <ArrowUpDown size={16} />
                    <select
                      value={completedSortBy}
                      onChange={(e) => setCompletedSortBy(e.target.value)}
                    >
                      <option value="date_desc">Most Recent</option>
                      <option value="date_asc">Oldest First</option>
                    </select>
                  </div>
                </SortRow>

                {isLoadingCompletedTasks ? (
                  <List>
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: "14px",
                          padding: "14px",
                          background: "#fff",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            marginBottom: "8px",
                          }}
                        >
                          <Skeleton h="18px" w="200px" />
                          <div style={{ marginLeft: "auto" }}>
                            <Skeleton h="18px" w="60px" />
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <Skeleton
                            h="24px"
                            w="80px"
                            style={{ borderRadius: "999px" }}
                          />
                          <Skeleton
                            h="24px"
                            w="100px"
                            style={{ borderRadius: "999px" }}
                          />
                          <Skeleton
                            h="24px"
                            w="70px"
                            style={{ borderRadius: "999px" }}
                          />
                        </div>
                      </div>
                    ))}
                  </List>
                ) : sortedCompletedTasks.length ? (
                  <List>
                    {sortedCompletedTasks.map((task) => (
                      <Item key={task.id} to={`/tasks/${task.id}`}>
                        <ItemTop>
                          <ItemTitle style={{ textTransform: "capitalize" }}>
                            {task.title}
                          </ItemTitle>
                          <Price>${task.offer_amount || task.budget}</Price>
                        </ItemTop>
                        <ItemMeta>
                          {getCategoryName(task.category_id) && (
                            <Badge style={{ 
                              background: theme.colors.primaryLight, 
                              color: theme.colors.primary,
                              fontWeight: '600'
                            }}>
                              <Briefcase size={14} />
                              {getCategoryName(task.category_id)}
                            </Badge>
                          )}
                          <Badge>
                            <MapPin size={14} />
                            {task.location || "Remote"}
                          </Badge>
                          <Badge>
                            <Calendar size={14} />
                            Completed{" "}
                            {formatDistanceToNow(
                              parseDate(task.completed_at || task.updated_at),
                              {
                                addSuffix: true,
                              }
                            )}
                          </Badge>
                          {task.poster && (
                            <Badge>
                              <User size={14} />
                              For{" "}
                              <Link to={`/users/${task.poster.id}`} style={{ textTransform: "capitalize", color: 'inherit', textDecoration: 'none' }}>
                                {task.poster.first_name} {task.poster.last_name}
                              </Link>
                            </Badge>
                          )}
                          {task.review && (
                            <Badge style={{
                              background: '#fef3c7',
                              color: '#92400e',
                              fontWeight: '600'
                            }}>
                              <Star size={14} style={{ fill: '#f59e0b', color: '#f59e0b' }} />
                              {task.review.rating}.0 rating
                            </Badge>
                          )}
                        </ItemMeta>
                      </Item>
                    ))}
                  </List>
                ) : (
                  <Empty>No completed work yet.</Empty>
                )}
              </Card>
            )}
          </ContentGrid>
        </Container>
      </Page>
    </>
  );
};

const mapStateToProps = (state) => ({ userState: state });
export default connect(mapStateToProps)(UserProfilePage);
